import { PageBuilderRowPropertiesModel, PageBuilderWidgetModel } from "@de-trustleap/shared-models";
import { createElementFromWidget as originalCreateElementFromWidget } from "../render/createElementFromWidget";
import { BaseWidget } from "./baseWidget";

async function createElementFromWidget(widget: PageBuilderWidgetModel, isInsideRow: boolean = false): Promise<HTMLElement | null> {
    if (isInsideRow) {
        (widget as any).applyStylesExternally = false;
    }
    return await originalCreateElementFromWidget(widget, isInsideRow);
}

export class RowWidget extends BaseWidget<PageBuilderRowPropertiesModel> {
    constructor(
        properties: PageBuilderRowPropertiesModel,
        private children: PageBuilderWidgetModel[] = [],
        private maxWidth?: number,
        private padding: { top?: number; right?: number; bottom?: number; left?: number } = {},
        private margin: { top?: number; right?: number; bottom?: number; left?: number } = {}
    ) {
        super(properties);
    }

    async render(): Promise<HTMLElement> {
        const element = this.createRowContainer();
        const fragment = await this.buildChildren();
        element.appendChild(fragment);
        return element;
    }

    private createRowContainer(): HTMLElement {
        const element = document.createElement("div");
        element.classList.add("row-widget");

        this.applyStyles(element, {
            display: "flex",
            flexDirection: "row",
            boxSizing: "border-box",
            flexWrap: "nowrap",
            justifyContent: this.mapMainAxisAlignment(this.properties?.mainAxisAlignment || "start"),
            alignItems: this.properties?.equalHeights === true
                ? "stretch"
                : this.mapCrossAxisAlignment(this.properties?.crossAxisAlignment || "center"),
            height: "100%"
        });

        if (this.maxWidth) {
            this.applyStyles(element, {
                maxWidth: `${this.maxWidth}px`,
                marginLeft: "auto",
                marginRight: "auto"
            });
        }

        return element;
    }

    private async buildChildren(): Promise<DocumentFragment> {
        const fragment = document.createDocumentFragment();

        for (const child of this.children) {
            const childElement = await createElementFromWidget(child, true);
            if (!childElement) continue;

            childElement.setAttribute("data-in-row", "true");

            const horizontalMargin = (child.margin?.left ?? 0) + (child.margin?.right ?? 0);
            const adjustedWidth = child.widthPercentage
                ? `calc(${child.widthPercentage}% - ${horizontalMargin}px)`
                : "auto";

            this.applyStyles(childElement, {
                flexGrow: child.widthPercentage ? "1" : "0",
                flexShrink: "1",
                flexBasis: adjustedWidth,
                boxSizing: "border-box",
                maxWidth: adjustedWidth,
                width: adjustedWidth,

            });
            this.applySpacing(childElement, child.padding, child.margin);

            if (child.widthPercentage) {
                childElement.classList.add("responsive-column");
            }

            fragment.appendChild(childElement);
        }

        return fragment;
    }

    private mapMainAxisAlignment = (alignment: string): string => ({
        start: "flex-start",
        end: "flex-end",
        spaceBetween: "space-between",
        spaceAround: "space-around",
        spaceEvenly: "space-evenly",
        center: "center"
    }[alignment] || "center");

    private mapCrossAxisAlignment = (alignment: string): string => ({
        start: "flex-start",
        end: "flex-end",
        stretch: "stretch",
        center: "center"
    }[alignment] || "center");
}
